import "./styles.css";
import {
  PublicClientApplication,
  BrowserCacheLocation,
  LogLevel
} from "@azure/msal-browser";
import {
  APP_NAME,
  APP_VERSION,
  GRAPH_SCOPES,
  CANONICAL_ROOT_LABEL,
  CATEGORY_OPTIONS,
  CATEGORY_TO_FOLDER,
  FOLDER_TO_CATEGORY,
  CONTENT_FOLDER_KEYS,
  LARGE_FILE_WARNING,
  HASH_MEMORY_LIMIT,
  TEXT_PREVIEW_LIMIT,
  classifyFile,
  extensionOf,
  fileTypeLabel,
  isTextLike
} from "./constants.js";
import {
  openDatabase,
  getAllLegacyCloudCache,
  clearLegacyCloudCache,
  getAllAssetCache,
  getAssetCache,
  putAssetCache,
  deleteAssetCache,
  getAllLocalFiles,
  getLocalFile,
  putLocalFile,
  deleteLocalFile
} from "./db.js";
import { GraphClient } from "./graph.js";
import {
  discoverRootKit,
  createRootKit,
  mutateManifest
} from "./rootkit.js";

const $ = id => document.getElementById(id);
const CLIENT_ID = import.meta.env.VITE_MS_CLIENT_ID || localStorage.getItem("qthub_ms_client_id") || "";
const AUTHORITY = import.meta.env.VITE_MS_AUTHORITY || "https://login.microsoftonline.com/common";
const REDIRECT_URI = `${window.location.origin}${window.location.pathname}`;

const state = {
  msal: null,
  graph: null,
  accountContexts: new Map(),
  records: [],
  localFiles: [],
  assetCache: new Map(),
  legacyRefMap: new Map(),
  pendingImportFiles: [],
  editingRecordKey: null,
  exportingRecordKey: null,
  previewRecordKey: null,
  replacingRecordKey: null,
  deletingRecordKey: null,
  objectUrls: new Set(),
  previewUrls: new Set(),
  transferStates: new Map(),
  busy: false
};

function uuid() {
  return crypto.randomUUID ? crypto.randomUUID() : `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
}

function formatDate(value) {
  if (!value) return "Unknown";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function baseDisplayName(filename) {
  return String(filename || "Untitled")
    .replace(/\.[^.]+$/, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function accountKey(account) {
  return account.homeAccountId;
}

function localCacheRef(accountId, itemId) {
  return `${accountId}:${itemId}`;
}

function showNotice(message, type = "") {
  const el = $("notice");
  el.textContent = message;
  el.className = `notice show ${type}`.trim();
  clearTimeout(showNotice.timer);
  showNotice.timer = setTimeout(() => { el.className = "notice"; }, 9000);
}

function setBusy(value) {
  state.busy = value;
  for (const id of ["addFilesBtn", "addFolderBtn", "refreshBtn", "connectBtn"]) {
    $(id).disabled = value;
  }
}

function registerObjectUrl(blob, lifetimeMs = 60 * 60 * 1000) {
  const url = URL.createObjectURL(blob);
  state.objectUrls.add(url);
  if (lifetimeMs) {
    setTimeout(() => {
      URL.revokeObjectURL(url);
      state.objectUrls.delete(url);
    }, lifetimeMs);
  }
  return url;
}

function registerPreviewUrl(blob) {
  const url = URL.createObjectURL(blob);
  state.previewUrls.add(url);
  return url;
}

function revokePreviewUrls() {
  for (const url of state.previewUrls) URL.revokeObjectURL(url);
  state.previewUrls.clear();
}

function revokeObjectUrls() {
  revokePreviewUrls();
  for (const url of state.objectUrls) URL.revokeObjectURL(url);
  state.objectUrls.clear();
}

function currentRecord(key) {
  return state.records.find(record => record.key === key) || null;
}

function getAccountContext(key) {
  return state.accountContexts.get(key) || null;
}

function displayEmail(profile, account) {
  return profile?.mail || profile?.userPrincipalName || account.username || profile?.displayName || "Microsoft account";
}

async function sha256Blob(blob) {
  if (!blob || blob.size > HASH_MEMORY_LIMIT) return "";
  const buffer = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return [...new Uint8Array(digest)].map(value => value.toString(16).padStart(2, "0")).join("");
}

function preferredAssetId(contentHash, knownByHash, existing = "") {
  if (contentHash && knownByHash.has(contentHash)) return knownByHash.get(contentHash);
  const id = existing || uuid();
  if (contentHash) knownByHash.set(contentHash, id);
  return id;
}

async function migrateLocalStorage() {
  const [locals, existingCaches, legacyCaches] = await Promise.all([
    getAllLocalFiles(),
    getAllAssetCache(),
    getAllLegacyCloudCache()
  ]);

  const knownByHash = new Map();
  for (const row of [...locals, ...existingCaches]) {
    if (row.contentHash && row.assetId) knownByHash.set(row.contentHash, row.assetId);
  }

  let changed = 0;
  for (const row of locals) {
    const contentHash = row.contentHash || await sha256Blob(row.blob);
    const assetId = preferredAssetId(contentHash, knownByHash, row.assetId);
    if (contentHash !== row.contentHash || assetId !== row.assetId) {
      row.contentHash = contentHash;
      row.assetId = assetId;
      await putLocalFile(row);
      changed += 1;
    }
  }

  for (const row of existingCaches) {
    const contentHash = row.contentHash || await sha256Blob(row.blob);
    const assetId = preferredAssetId(contentHash, knownByHash, row.assetId);
    if (contentHash !== row.contentHash || assetId !== row.assetId) {
      await putAssetCache({
        assetId,
        contentHash,
        name: row.name,
        mime: row.mime,
        blob: row.blob
      });
      if (row.assetId && assetId !== row.assetId) await deleteAssetCache(row.assetId);
      changed += 1;
    }
  }

  for (const row of legacyCaches) {
    const contentHash = row.contentHash || await sha256Blob(row.blob);
    const assetId = preferredAssetId(contentHash, knownByHash, row.assetId);
    await putAssetCache({
      assetId,
      contentHash,
      name: row.name,
      mime: row.mime,
      blob: row.blob
    });
    state.legacyRefMap.set(localCacheRef(row.accountKey, row.itemId), { assetId, contentHash });
    changed += 1;
  }

  if (legacyCaches.length) await clearLegacyCloudCache();
  if (changed) showNotice(`Version 2.2 migrated ${changed} local storage record${changed === 1 ? "" : "s"} into logical assets.`, "success");
}

async function initializeAuth() {
  if (!CLIENT_ID) {
    $("setupBanner").classList.remove("hidden");
    return;
  }
  if (window.location.protocol === "file:") {
    showNotice("The OneDrive version must run from HTTPS or localhost, not file://. See SETUP_CHECKLIST.md.", "warn");
    return;
  }

  state.msal = new PublicClientApplication({
    auth: {
      clientId: CLIENT_ID,
      authority: AUTHORITY,
      redirectUri: REDIRECT_URI,
      postLogoutRedirectUri: REDIRECT_URI,
      navigateToLoginRequestUrl: false
    },
    cache: { cacheLocation: BrowserCacheLocation.LocalStorage },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: false,
        loggerCallback: (_level, message, containsPii) => {
          if (!containsPii) console.debug(message);
        }
      }
    }
  });

  await state.msal.initialize();
  const redirectResult = await state.msal.handleRedirectPromise();
  state.graph = new GraphClient(state.msal);

  if (redirectResult?.account) {
    try {
      await loadAccount(redirectResult.account, { promptCreate: true });
      showNotice(`${redirectResult.account.username || "Microsoft account"} connected.`, "success");
    } catch (error) {
      console.error(error);
      showNotice(`Microsoft sign-in completed, but OneDrive could not be loaded: ${error.message}`, "error");
    }
  }
  await loadCachedAccounts();
}

async function loadCachedAccounts() {
  const accounts = state.msal?.getAllAccounts() || [];
  for (const account of accounts) {
    try {
      await loadAccount(account, { promptCreate: false });
    } catch (error) {
      console.error(error);
      showNotice(`Could not load ${account.username || "a Microsoft account"}: ${error.message}`, "warn");
    }
  }
  await refreshLibrary({ reloadCloud: false });
}

async function connectAccount() {
  if (!state.msal) {
    $("clientDialog").showModal();
    return;
  }
  try {
    setBusy(true);
    await state.msal.loginRedirect({
      scopes: GRAPH_SCOPES,
      prompt: "select_account",
      redirectStartPage: window.location.href
    });
  } catch (error) {
    setBusy(false);
    showNotice(`Microsoft sign-in failed: ${error.message}`, "error");
  }
}

async function loadAccount(account, { promptCreate = false } = {}) {
  const key = accountKey(account);
  let context = state.accountContexts.get(key);
  if (!context) {
    const [profile, drive] = await Promise.all([
      state.graph.getProfile(account),
      state.graph.getDrive(account)
    ]);
    context = {
      key,
      account,
      profile,
      drive,
      email: displayEmail(profile, account),
      rootKit: null,
      rootStatus: "checking",
      cloudFiles: []
    };
    state.accountContexts.set(key, context);
  }

  const rootKit = await discoverRootKit(state.graph, account);
  if (!rootKit) {
    context.rootStatus = "missing";
    context.rootKit = null;
    context.cloudFiles = [];
    if (promptCreate) {
      const accepted = confirm(`The standard root was not found in ${context.email}.\n\nCreate ${CANONICAL_ROOT_LABEL}?`);
      if (accepted) await createRootForContext(context);
    }
  } else {
    context.rootStatus = "ready";
    context.rootKit = rootKit;
    await loadCloudFilesForContext(context);
  }
  renderAccounts();
  renderAccountFilter();
  return context;
}

async function createRootForContext(context) {
  showNotice(`Creating the standard root kit in ${context.email}...`);
  context.rootKit = await createRootKit(state.graph, context.account);
  context.rootStatus = "ready";
  await loadCloudFilesForContext(context);
  renderAccounts();
  renderAccountFilter();
  showNotice(`Root kit created in ${context.email}.`, "success");
}

function hashAssetMap() {
  const map = new Map();
  for (const row of state.localFiles) if (row.contentHash && row.assetId) map.set(row.contentHash, row.assetId);
  for (const row of state.assetCache.values()) if (row.contentHash && row.assetId) map.set(row.contentHash, row.assetId);
  for (const context of state.accountContexts.values()) {
    for (const row of context.cloudFiles || []) if (row.contentHash && row.assetId) map.set(row.contentHash, row.assetId);
  }
  return map;
}

async function loadCloudFilesForContext(context) {
  if (!context.rootKit) {
    context.cloudFiles = [];
    return;
  }

  const files = [];
  const manifestUpdates = new Map();
  const knownByHash = hashAssetMap();

  for (const folderKey of CONTENT_FOLDER_KEYS) {
    const folderId = context.rootKit.folders[folderKey];
    const children = await state.graph.listChildren(context.account, folderId);
    for (const item of children) {
      if (!item.file) continue;
      const metadata = context.rootKit.manifest.items?.[item.id] || {};
      const legacy = state.legacyRefMap.get(localCacheRef(context.key, item.id));
      let contentHash = metadata.contentHash || legacy?.contentHash || "";
      let assetId = metadata.assetId || legacy?.assetId || "";
      if (contentHash && knownByHash.has(contentHash)) assetId = knownByHash.get(contentHash);
      assetId = preferredAssetId(contentHash, knownByHash, assetId);

      const cache = state.assetCache.get(assetId);
      if (!contentHash && cache?.contentHash) contentHash = cache.contentHash;
      const changedExternally = Boolean(metadata.eTag && item.eTag && metadata.eTag !== item.eTag);

      const normalizedMetadata = {
        ...metadata,
        assetId,
        contentHash,
        eTag: changedExternally ? metadata.eTag : (item.eTag || metadata.eTag || ""),
        displayName: metadata.displayName || baseDisplayName(item.name),
        category: metadata.category || FOLDER_TO_CATEGORY[folderKey] || "Data",
        version: metadata.version || "1.0",
        description: metadata.description || "",
        tags: Array.isArray(metadata.tags) ? metadata.tags : [],
        favorite: Boolean(metadata.favorite),
        updatedAt: metadata.updatedAt || item.lastModifiedDateTime || new Date().toISOString()
      };

      if (
        metadata.assetId !== normalizedMetadata.assetId ||
        metadata.contentHash !== normalizedMetadata.contentHash ||
        metadata.eTag !== normalizedMetadata.eTag ||
        !metadata.category || !metadata.version
      ) manifestUpdates.set(item.id, normalizedMetadata);

      files.push({
        key: `cloud:${context.key}:${item.id}`,
        kind: "cloud",
        accountKey: context.key,
        accountEmail: context.email,
        itemId: item.id,
        item,
        eTag: item.eTag || "",
        manifestETag: metadata.eTag || "",
        changedExternally,
        assetId,
        contentHash,
        name: normalizedMetadata.displayName,
        originalName: item.name,
        category: normalizedMetadata.category,
        folderKey,
        version: normalizedMetadata.version,
        description: normalizedMetadata.description,
        tags: normalizedMetadata.tags,
        favorite: normalizedMetadata.favorite,
        mime: item.file.mimeType || "application/octet-stream",
        size: item.size || 0,
        modifiedAt: item.lastModifiedDateTime,
        metadataUpdatedAt: normalizedMetadata.updatedAt,
        webUrl: item.webUrl || ""
      });
    }
  }

  context.cloudFiles = files;
  if (manifestUpdates.size) {
    await mutateManifest(state.graph, context, manifest => {
      manifest.items ||= {};
      for (const [itemId, metadata] of manifestUpdates) manifest.items[itemId] = metadata;
    });
  }
}

function addMetadataCandidate(asset, location) {
  asset.metadataCandidates.push({
    name: location.name || baseDisplayName(location.originalName),
    originalName: location.originalName,
    category: location.category || "Data",
    version: location.version || "1.0",
    description: location.description || "",
    tags: location.tags || [],
    favorite: Boolean(location.favorite),
    mime: location.mime || "application/octet-stream",
    size: location.size || 0,
    modifiedAt: location.modifiedAt || new Date(0).toISOString(),
    metadataUpdatedAt: location.metadataUpdatedAt || location.modifiedAt || new Date(0).toISOString()
  });
}

function buildLogicalAssets() {
  const assets = new Map();
  const ensureAsset = assetId => {
    if (!assets.has(assetId)) {
      assets.set(assetId, {
        key: assetId,
        assetId,
        cloudLocations: [],
        localLocations: [],
        metadataCandidates: [],
        contentHashes: new Set(),
        cache: state.assetCache.get(assetId) || null,
        changedExternally: false
      });
    }
    return assets.get(assetId);
  };

  for (const file of state.localFiles) {
    const asset = ensureAsset(file.assetId || `local-${file.id}`);
    asset.localLocations.push({ kind: "local", localId: file.id, blob: file.blob, contentHash: file.contentHash || "" });
    if (file.contentHash) asset.contentHashes.add(file.contentHash);
    addMetadataCandidate(asset, file);
  }

  for (const context of state.accountContexts.values()) {
    for (const file of context.cloudFiles || []) {
      const asset = ensureAsset(file.assetId);
      asset.cloudLocations.push(file);
      asset.changedExternally ||= file.changedExternally;
      if (file.contentHash) asset.contentHashes.add(file.contentHash);
      addMetadataCandidate(asset, file);
    }
  }

  for (const cache of state.assetCache.values()) {
    const asset = ensureAsset(cache.assetId);
    asset.cache = cache;
    if (cache.contentHash) asset.contentHashes.add(cache.contentHash);
    if (!asset.metadataCandidates.length) {
      addMetadataCandidate(asset, {
        name: baseDisplayName(cache.name),
        originalName: cache.name,
        category: FOLDER_TO_CATEGORY[classifyFile(cache.name)] || "Data",
        version: "1.0",
        description: "",
        tags: [],
        favorite: false,
        mime: cache.mime,
        size: cache.size,
        modifiedAt: cache.cachedAt
      });
    }
  }

  const result = [];
  for (const asset of assets.values()) {
    asset.metadataCandidates.sort((a, b) => new Date(b.metadataUpdatedAt) - new Date(a.metadataUpdatedAt));
    const meta = asset.metadataCandidates[0] || {};
    Object.assign(asset, {
      name: meta.name || "Untitled",
      originalName: meta.originalName || asset.cache?.name || "file.bin",
      category: meta.category || "Data",
      version: meta.version || "1.0",
      description: meta.description || "",
      tags: meta.tags || [],
      favorite: Boolean(meta.favorite),
      mime: meta.mime || asset.cache?.mime || "application/octet-stream",
      size: meta.size || asset.cache?.size || 0,
      modifiedAt: meta.modifiedAt || asset.cache?.cachedAt || new Date(0).toISOString(),
      contentHash: [...asset.contentHashes][0] || asset.cache?.contentHash || "",
      syncConflict: asset.contentHashes.size > 1,
      downloaded: Boolean(asset.cache || asset.localLocations.length),
      accountKeys: [...new Set(asset.cloudLocations.map(x => x.accountKey))],
      accountEmails: [...new Set(asset.cloudLocations.map(x => x.accountEmail))]
    });
    result.push(asset);
  }

  result.sort((a, b) => (Number(b.favorite) - Number(a.favorite)) || new Date(b.modifiedAt) - new Date(a.modifiedAt));
  return result;
}

async function refreshLibrary({ reloadCloud = true } = {}) {
  try {
    state.localFiles = await getAllLocalFiles();
    const caches = await getAllAssetCache();
    state.assetCache = new Map(caches.map(row => [row.assetId, row]));

    if (reloadCloud && state.graph) {
      for (const context of state.accountContexts.values()) {
        try {
          await loadAccount(context.account, { promptCreate: false });
        } catch (error) {
          console.error(error);
          showNotice(`Refresh failed for ${context.email}: ${error.message}`, "warn");
        }
      }
    } else {
      for (const context of state.accountContexts.values()) {
        if (context.rootKit) await loadCloudFilesForContext(context);
      }
    }

    state.records = buildLogicalAssets();
    renderAll();
  } catch (error) {
    console.error(error);
    showNotice(`Library refresh failed: ${error.message}`, "error");
  }
}

function renderAll() {
  renderAccounts();
  renderAccountFilter();
  renderCategoryOptions();
  renderStatus();
  renderCards();
}

function renderStatus() {
  $("accountCount").textContent = state.accountContexts.size;
  $("cloudCount").textContent = state.records.filter(x => x.cloudLocations.length).length;
  $("localCount").textContent = state.records.filter(x => !x.cloudLocations.length && x.downloaded).length;
  $("downloadedCount").textContent = state.records.filter(x => x.downloaded).length;
}

function renderAccountFilter() {
  const select = $("accountFilter");
  const previous = select.value;
  select.innerHTML = `
    <option value="all">All connected accounts</option>
    <option value="local">Downloaded/local files</option>
    ${[...state.accountContexts.values()].map(ctx => `<option value="${escapeHtml(ctx.key)}">${escapeHtml(ctx.email)}</option>`).join("")}
  `;
  if ([...select.options].some(option => option.value === previous)) select.value = previous;
}

function renderCategoryOptions() {
  const filters = ["all", ...CATEGORY_OPTIONS];
  const categorySelect = $("categoryFilter");
  const previous = categorySelect.value;
  categorySelect.innerHTML = filters.map(x => `<option value="${escapeHtml(x)}">${x === "all" ? "All categories" : escapeHtml(x)}</option>`).join("");
  if (filters.includes(previous)) categorySelect.value = previous;

  for (const id of ["editCategory", "exportCategory"]) {
    const select = $(id);
    const selected = select.value;
    select.innerHTML = CATEGORY_OPTIONS.map(x => `<option value="${escapeHtml(x)}">${escapeHtml(x)}</option>`).join("");
    if (CATEGORY_OPTIONS.includes(selected)) select.value = selected;
  }
}

function renderAccounts() {
  const list = $("accountsList");
  const contexts = [...state.accountContexts.values()];
  if (!contexts.length) {
    list.innerHTML = `<div class="summary-box">No Microsoft accounts are connected on this device.</div>`;
    return;
  }

  list.innerHTML = contexts.map(ctx => {
    const quota = ctx.drive?.quota;
    const quotaText = quota?.total
      ? `${formatBytes(quota.used || 0)} used of ${formatBytes(quota.total)}`
      : "Storage quota unavailable";
    const fileCount = ctx.cloudFiles?.length || 0;
    return `
      <article class="account-card">
        <div class="account-card-header">
          <div>
            <div class="account-email">${escapeHtml(ctx.email)}</div>
            <div class="account-meta">${escapeHtml(ctx.profile?.displayName || "")} · ${escapeHtml(quotaText)} · ${fileCount} hub files</div>
          </div>
          <span class="root-status ${ctx.rootStatus === "ready" ? "ready" : "missing"}">${ctx.rootStatus === "ready" ? "ROOT FOUND" : "ROOT MISSING"}</span>
        </div>
        <div class="account-meta">${escapeHtml(CANONICAL_ROOT_LABEL)}</div>
        <div class="account-actions">
          ${ctx.rootStatus === "missing" ? `<button class="button primary" data-account-action="create-root" data-account-key="${escapeHtml(ctx.key)}">Create Root Kit</button>` : ""}
          <button class="button" data-account-action="refresh" data-account-key="${escapeHtml(ctx.key)}">Refresh</button>
          ${ctx.rootKit?.root?.webUrl ? `<button class="button" data-account-action="open-onedrive" data-account-key="${escapeHtml(ctx.key)}">Open in OneDrive</button>` : ""}
        </div>
      </article>`;
  }).join("");
}

function transferStateFor(asset) {
  return state.transferStates.get(asset.key) || null;
}

function statusFor(asset) {
  const transfer = transferStateFor(asset);
  if (transfer?.type === "downloading") return { label: transfer.progress ? `DOWNLOADING ${Math.round(transfer.progress * 100)}%` : "DOWNLOADING", cls: "pending" };
  if (transfer?.type === "uploading") return { label: `UPLOADING ${Math.round((transfer.progress || 0) * 100)}%`, cls: "pending" };
  if (transfer?.type === "failed") return { label: "DOWNLOAD FAILED", cls: "failed" };
  if (asset.syncConflict) return { label: "SYNC CONFLICT", cls: "failed" };
  if (asset.changedExternally) return { label: "UPDATE AVAILABLE", cls: "pending" };
  return asset.downloaded
    ? { label: "DOWNLOADED", cls: "downloaded" }
    : { label: "NOT DOWNLOADED", cls: "not-downloaded" };
}

function filteredRecords() {
  const query = $("searchInput").value.trim().toLowerCase();
  const account = $("accountFilter").value;
  const category = $("categoryFilter").value;
  const favorites = $("favoritesOnly").checked;

  return state.records.filter(record => {
    if (account === "local" && !record.downloaded) return false;
    if (account !== "all" && account !== "local" && !record.accountKeys.includes(account)) return false;
    if (category !== "all" && record.category !== category) return false;
    if (favorites && !record.favorite) return false;
    if (!query) return true;
    const haystack = [
      record.name,
      record.originalName,
      record.category,
      record.version,
      record.description,
      ...record.accountEmails,
      ...(record.tags || [])
    ].join(" ").toLowerCase();
    return haystack.includes(query);
  });
}

function locationText(asset) {
  const locations = [];
  if (asset.downloaded) locations.push("Local cache");
  locations.push(...asset.accountEmails);
  return locations.length ? `Saved to: ${locations.join(" · ")}` : "No active storage location";
}

function renderCards() {
  const grid = $("fileGrid");
  const records = filteredRecords();
  if (!records.length) {
    grid.innerHTML = `<div class="empty-state">${state.records.length ? "No files match the current filters." : "No files are available. Connect a Microsoft account or import local files."}</div>`;
    return;
  }

  grid.innerHTML = records.map(record => {
    const status = statusFor(record);
    const tags = (record.tags || []).map(tag => `<span class="tag">${escapeHtml(tag)}</span>`).join("");
    return `
      <article class="file-card">
        <div class="card-header">
          <h3 class="card-title">${escapeHtml(record.name || record.originalName)}</h3>
          <button class="favorite-button ${record.favorite ? "active" : ""}" data-action="favorite" data-key="${escapeHtml(record.key)}" title="Toggle favorite">${record.favorite ? "★" : "☆"}</button>
        </div>
        <div class="classification-row">
          <span class="classification-left">${escapeHtml(fileTypeLabel(record.originalName, record.mime))} · ${escapeHtml(record.category)} · ${escapeHtml(formatBytes(record.size))}</span>
          <span class="download-state ${status.cls}">${escapeHtml(status.label)}</span>
        </div>
        <div class="source-line">${escapeHtml(locationText(record))}</div>
        <div class="version-line">Version ${escapeHtml(record.version || "1.0")} · Modified ${escapeHtml(formatDate(record.modifiedAt))}</div>
        <div class="tag-row"><span class="tag">.${escapeHtml(extensionOf(record.originalName) || "file")}</span>${tags}</div>
        <div class="description">${escapeHtml(record.description || "No description.")}</div>
        <div class="card-actions">
          <button class="button primary" data-action="open" data-key="${escapeHtml(record.key)}">Open</button>
          <button class="button primary subtle" data-action="preview" data-key="${escapeHtml(record.key)}">Preview</button>
          <button class="button" data-action="edit" data-key="${escapeHtml(record.key)}">Edit</button>
          <button class="button" data-action="export" data-key="${escapeHtml(record.key)}">Export</button>
          <button class="button" data-action="replace" data-key="${escapeHtml(record.key)}">Replace</button>
          <button class="button danger" data-action="delete" data-key="${escapeHtml(record.key)}">Delete</button>
        </div>
      </article>`;
  }).join("");
}

function firstConnectedCloudLocation(asset) {
  return asset.cloudLocations.find(location => getAccountContext(location.accountKey)) || null;
}

async function updateCloudLocationIdentity(asset, location, contentHash, eTag) {
  const context = getAccountContext(location.accountKey);
  if (!context) return;
  await mutateManifest(state.graph, context, manifest => {
    manifest.items ||= {};
    const metadata = manifest.items[location.itemId] || {};
    manifest.items[location.itemId] = {
      ...metadata,
      assetId: asset.assetId,
      contentHash,
      eTag: eTag || location.eTag || metadata.eTag || "",
      updatedAt: new Date().toISOString()
    };
  });
}

async function ensureBlob(asset, { cache = true } = {}) {
  if (asset.localLocations.length) {
    const local = await getLocalFile(asset.localLocations[0].localId);
    if (local?.blob) return local.blob;
  }

  const cached = await getAssetCache(asset.assetId);
  if (cached?.blob && !asset.changedExternally) return cached.blob;

  const location = asset.cloudLocations.find(row => row.changedExternally && getAccountContext(row.accountKey)) || firstConnectedCloudLocation(asset);
  if (!location) {
    if (cached?.blob) return cached.blob;
    throw new Error("No connected OneDrive location or local copy is available for this asset.");
  }

  const context = getAccountContext(location.accountKey);
  state.transferStates.set(asset.key, { type: "downloading", progress: 0 });
  renderCards();
  try {
    const blob = await state.graph.downloadBlob(context.account, location.itemId);
    const contentHash = await sha256Blob(blob);
    if (cache && blob.size <= LARGE_FILE_WARNING) {
      await putAssetCache({
        assetId: asset.assetId,
        contentHash,
        name: location.originalName,
        mime: location.mime,
        blob
      });
    }
    await updateCloudLocationIdentity(asset, location, contentHash, location.eTag);
    state.transferStates.delete(asset.key);
    await refreshLibrary({ reloadCloud: false });
    return blob;
  } catch (error) {
    state.transferStates.set(asset.key, { type: "failed" });
    renderCards();
    throw error;
  }
}

function writeWindowDocument(win, html) {
  win.document.open();
  win.document.write(html);
  win.document.close();
  try { win.opener = null; } catch { /* ignored */ }
}

function sandboxWrapper(title, htmlText) {
  const payload = JSON.stringify(htmlText).replaceAll("<", "\\u003c");
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>html,body,iframe{width:100%;height:100%;margin:0;border:0;background:#fff}body{overflow:hidden}</style></head><body><iframe id="tool" sandbox="allow-scripts allow-forms allow-downloads allow-modals"></iframe><script>document.getElementById("tool").srcdoc=${payload};<\/script></body></html>`;
}

function textWrapper(title, text) {
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>body{margin:0;background:#0b0f14;color:#edf2f7;font:13px/1.5 Consolas,monospace}header{position:sticky;top:0;padding:12px 16px;background:#121821;border-bottom:1px solid #2a3543;font:600 15px system-ui}pre{white-space:pre-wrap;overflow-wrap:anywhere;margin:0;padding:18px}</style></head><body><header>${escapeHtml(title)}</header><pre>${escapeHtml(text)}</pre></body></html>`;
}

async function openRecord(asset) {
  const win = window.open("about:blank", "_blank");
  if (!win) {
    showNotice("The browser blocked the Open tab. Allow pop-ups for the Tool Hub and try again.", "warn");
    return;
  }
  writeWindowDocument(win, `<!doctype html><title>Opening ${escapeHtml(asset.name)}</title><body style="background:#0b0f14;color:#edf2f7;font-family:system-ui;padding:24px">Opening ${escapeHtml(asset.name)}...</body>`);

  try {
    const ext = extensionOf(asset.originalName);
    if (!["html", "htm"].includes(ext) && !isTextLike(asset.originalName, asset.mime) && !asset.mime.startsWith("image/") && asset.mime !== "application/pdf" && ext !== "pdf") {
      const cloud = firstConnectedCloudLocation(asset);
      if (cloud?.webUrl) {
        win.location.replace(cloud.webUrl);
        return;
      }
    }

    const blob = await ensureBlob(asset, { cache: true });
    if (["html", "htm"].includes(ext)) {
      writeWindowDocument(win, sandboxWrapper(asset.name, await blob.text()));
      return;
    }
    if (isTextLike(asset.originalName, asset.mime)) {
      const openLimit = 20 * 1024 * 1024;
      let text = await blob.slice(0, openLimit).text();
      if (blob.size > openLimit) text += `\n\n[Open view truncated at 20 MB.]`;
      writeWindowDocument(win, textWrapper(asset.name, text));
      return;
    }
    if (asset.mime.startsWith("image/") || asset.mime === "application/pdf" || ext === "pdf") {
      win.location.replace(registerObjectUrl(blob));
      return;
    }
    const cloud = firstConnectedCloudLocation(asset);
    if (cloud?.webUrl) {
      win.location.replace(cloud.webUrl);
      return;
    }
    win.close();
    showNotice("This file type cannot be opened directly in the browser. Use Export to save a physical copy.", "warn");
  } catch (error) {
    try { win.close(); } catch { /* ignored */ }
    showNotice(`Open failed: ${error.message}`, "error");
  }
}

async function previewRecord(asset) {
  state.previewRecordKey = asset.key;
  $("previewTitle").textContent = asset.name || asset.originalName;
  $("previewBody").innerHTML = `<div class="summary-box">Downloading and preparing preview...</div>`;
  $("previewDialog").showModal();
  try {
    revokePreviewUrls();
    const blob = await ensureBlob(asset, { cache: true });
    const ext = extensionOf(asset.originalName);
    if (["html", "htm"].includes(ext)) {
      const url = registerPreviewUrl(blob);
      $("previewBody").innerHTML = `<iframe class="preview-frame" sandbox="allow-scripts allow-forms allow-downloads" src="${url}"></iframe>`;
      return;
    }
    if (isTextLike(asset.originalName, asset.mime)) {
      const previewBlob = blob.slice(0, TEXT_PREVIEW_LIMIT);
      let text = await previewBlob.text();
      if (blob.size > TEXT_PREVIEW_LIMIT) text += "\n\n[Preview truncated at 2 MB.]";
      $("previewBody").innerHTML = `<pre class="text-preview">${escapeHtml(text)}</pre>`;
      return;
    }
    if (asset.mime.startsWith("image/")) {
      const url = registerPreviewUrl(blob);
      $("previewBody").innerHTML = `<img class="image-preview" src="${url}" alt="${escapeHtml(asset.name)}">`;
      return;
    }
    if (asset.mime === "application/pdf" || ext === "pdf") {
      const url = registerPreviewUrl(blob);
      $("previewBody").innerHTML = `<iframe class="preview-frame" src="${url}"></iframe>`;
      return;
    }
    $("previewBody").innerHTML = `<div class="summary-box">No internal preview is available for this file type. Use Open for its OneDrive web view or Export to save a local copy.</div>`;
  } catch (error) {
    $("previewBody").innerHTML = `<div class="notice show error">Preview failed: ${escapeHtml(error.message)}</div>`;
  }
}

function openEdit(asset) {
  state.editingRecordKey = asset.key;
  $("editName").value = asset.name || baseDisplayName(asset.originalName);
  $("editCategory").value = CATEGORY_OPTIONS.includes(asset.category) ? asset.category : "Data";
  $("editVersion").value = asset.version || "1.0";
  $("editTags").value = (asset.tags || []).join(", ");
  $("editDescription").value = asset.description || "";
  $("editFavorite").checked = Boolean(asset.favorite);
  $("editReadonly").innerHTML = `<span>Logical asset</span>${escapeHtml(asset.originalName)} · ${escapeHtml(formatBytes(asset.size))} · ${escapeHtml(locationText(asset))}`;
  $("editDialog").showModal();
}

async function saveAssetMetadata(asset, next) {
  for (const location of asset.localLocations) {
    const local = await getLocalFile(location.localId);
    if (!local) continue;
    Object.assign(local, next, { modifiedAt: new Date().toISOString() });
    await putLocalFile(local);
  }

  const grouped = new Map();
  for (const location of asset.cloudLocations) {
    if (!grouped.has(location.accountKey)) grouped.set(location.accountKey, []);
    grouped.get(location.accountKey).push(location);
  }

  for (const [key, locations] of grouped) {
    const context = getAccountContext(key);
    if (!context) continue;
    const destinationKey = CATEGORY_TO_FOLDER[next.category];
    const eTags = new Map();
    for (const location of locations) {
      if (destinationKey && destinationKey !== location.folderKey) {
        const moved = await state.graph.moveItem(context.account, location.itemId, context.rootKit.folders[destinationKey]);
        eTags.set(location.itemId, moved.eTag || location.eTag || "");
      }
    }
    await mutateManifest(state.graph, context, manifest => {
      manifest.items ||= {};
      for (const location of locations) {
        manifest.items[location.itemId] = {
          ...(manifest.items[location.itemId] || {}),
          assetId: asset.assetId,
          contentHash: asset.contentHash,
          eTag: eTags.get(location.itemId) || location.eTag || "",
          displayName: next.name,
          category: next.category,
          version: next.version,
          tags: next.tags,
          description: next.description,
          favorite: next.favorite,
          updatedAt: new Date().toISOString()
        };
      }
    });
  }
}

async function saveEdit() {
  const asset = currentRecord(state.editingRecordKey);
  if (!asset) return;
  const next = {
    name: $("editName").value.trim() || baseDisplayName(asset.originalName),
    category: $("editCategory").value,
    version: $("editVersion").value.trim() || "1.0",
    tags: $("editTags").value.split(",").map(x => x.trim()).filter(Boolean),
    description: $("editDescription").value.trim(),
    favorite: $("editFavorite").checked
  };

  try {
    setBusy(true);
    await saveAssetMetadata(asset, next);
    $("editDialog").close();
    await refreshLibrary({ reloadCloud: true });
    showNotice("Asset details updated across its saved locations.", "success");
  } catch (error) {
    showNotice(`Could not save changes: ${error.message}`, "error");
  } finally {
    setBusy(false);
  }
}

async function toggleFavorite(asset) {
  await saveAssetMetadata(asset, {
    name: asset.name,
    category: asset.category,
    version: asset.version,
    tags: asset.tags,
    description: asset.description,
    favorite: !asset.favorite
  });
  await refreshLibrary({ reloadCloud: true });
}

function renderAccountChecks(containerId, prefix, { readyOnly = true } = {}) {
  const contexts = [...state.accountContexts.values()];
  const container = $(containerId);
  if (!contexts.length) {
    container.innerHTML = `<div class="muted">No Microsoft accounts are connected.</div>`;
    return;
  }
  container.innerHTML = contexts.map(ctx => {
    const disabled = readyOnly && ctx.rootStatus !== "ready";
    return `<label>
      <input type="checkbox" name="${prefix}" value="${escapeHtml(ctx.key)}" ${disabled ? "disabled" : ""}>
      <span class="account-detail"><strong>${escapeHtml(ctx.email)}</strong><small>${ctx.rootStatus === "ready" ? CANONICAL_ROOT_LABEL : "Root kit not found"}</small></span>
    </label>`;
  }).join("");
}

function openImport(files) {
  state.pendingImportFiles = [...files].filter(file => file?.name);
  if (!state.pendingImportFiles.length) return;
  const total = state.pendingImportFiles.reduce((sum, file) => sum + file.size, 0);
  const large = state.pendingImportFiles.filter(file => file.size > LARGE_FILE_WARNING).length;
  $("importSummary").innerHTML = `<strong>${state.pendingImportFiles.length} file${state.pendingImportFiles.length === 1 ? "" : "s"}</strong> · ${formatBytes(total)}${large ? ` · ${large} above 200 MB` : ""}`;
  $("importProgress").innerHTML = "";
  $("keepLocalCopy").checked = large === 0;
  $("createLocalOnly").checked = false;
  renderAccountChecks("importAccounts", "importAccount");
  $("importDialog").showModal();
}

function selectedCheckboxValues(name) {
  return [...document.querySelectorAll(`input[name="${name}"]:checked`)].map(x => x.value);
}

function progressRow(id, label) {
  return `<div class="progress-item" id="${escapeHtml(id)}"><span>${escapeHtml(label)}</span><strong>0%</strong><div class="progress-track"><div class="progress-bar"></div></div></div>`;
}

function updateProgress(id, progress, label = null) {
  const row = document.getElementById(id);
  if (!row) return;
  const pct = Math.max(0, Math.min(100, Math.round(progress * 100)));
  row.querySelector("strong").textContent = label || `${pct}%`;
  row.querySelector(".progress-bar").style.width = `${pct}%`;
}

function findAssetByHash(contentHash) {
  if (!contentHash) return null;
  return state.records.find(asset => asset.contentHash === contentHash && !asset.syncConflict) || null;
}

async function createLocalRecord(file, identity, metadata) {
  const alreadyLocal = identity.localOnlyCreated || state.localFiles.some(row => row.assetId === identity.assetId);
  if (alreadyLocal) return null;
  const now = new Date().toISOString();
  const folderKey = classifyFile(file.name);
  const record = {
    id: uuid(),
    assetId: identity.assetId,
    contentHash: identity.contentHash,
    name: metadata.name || baseDisplayName(file.name),
    originalName: file.name,
    category: metadata.category || FOLDER_TO_CATEGORY[folderKey] || "Data",
    version: metadata.version || "1.0",
    description: metadata.description || "",
    tags: metadata.tags || [],
    favorite: Boolean(metadata.favorite),
    mime: file.type || "application/octet-stream",
    size: file.size,
    addedAt: now,
    modifiedAt: now,
    blob: file
  };
  await putLocalFile(record);
  identity.localOnlyCreated = true;
  return record;
}

async function uploadAssetToContext(context, file, identity, metadata, onProgress) {
  if (!context.rootKit) throw new Error(`The root kit is missing in ${context.email}.`);
  const existing = identity.accountKeys?.has(context.key) || state.records.find(asset => asset.assetId === identity.assetId)?.cloudLocations.find(location => location.accountKey === context.key);
  if (existing) {
    onProgress?.(1, "Already saved");
    return typeof existing === "object" ? existing.item : null;
  }

  const folderKey = CATEGORY_TO_FOLDER[metadata.category] || classifyFile(file.name);
  const item = await state.graph.uploadNew(context.account, context.rootKit.folders[folderKey], file, {
    onProgress: value => onProgress?.(value)
  });
  if (!item?.id) throw new Error(`OneDrive did not return an item ID for ${file.name}.`);
  await mutateManifest(state.graph, context, manifest => {
    manifest.items ||= {};
    manifest.items[item.id] = {
      assetId: identity.assetId,
      contentHash: identity.contentHash,
      eTag: item.eTag || "",
      displayName: metadata.name || baseDisplayName(item.name || file.name),
      category: metadata.category || FOLDER_TO_CATEGORY[folderKey] || "Data",
      version: metadata.version || "1.0",
      description: metadata.description || "",
      tags: metadata.tags || [],
      favorite: Boolean(metadata.favorite),
      updatedAt: new Date().toISOString()
    };
  });
  identity.accountKeys?.add(context.key);
  return item;
}

async function startImport() {
  const files = state.pendingImportFiles;
  const accountKeys = selectedCheckboxValues("importAccount");
  const keepLocal = $("keepLocalCopy").checked;
  const keepIndependentLocal = $("createLocalOnly").checked || accountKeys.length === 0;
  if (!files.length) return;

  try {
    setBusy(true);
    $("startImportBtn").disabled = true;
    const rows = [];
    if (keepIndependentLocal) files.forEach((file, i) => rows.push(progressRow(`import-local-${i}`, `${file.name} → Local cache`)));
    for (const key of accountKeys) {
      const context = getAccountContext(key);
      files.forEach((file, i) => rows.push(progressRow(`import-${key}-${i}`, `${file.name} → ${context.email}`)));
    }
    $("importProgress").innerHTML = rows.join("");

    const batchByHash = new Map();
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const contentHash = await sha256Blob(file);
      const existingAsset = findAssetByHash(contentHash);
      let identity = contentHash ? batchByHash.get(contentHash) : null;
      if (!identity) {
        identity = {
          assetId: existingAsset?.assetId || uuid(),
          contentHash,
          accountKeys: new Set(existingAsset?.accountKeys || []),
          localOnlyCreated: Boolean(existingAsset?.localLocations?.length)
        };
        if (contentHash) batchByHash.set(contentHash, identity);
      }
      const folderKey = classifyFile(file.name);
      const metadata = existingAsset ? {
        name: existingAsset.name,
        category: existingAsset.category,
        version: existingAsset.version,
        description: existingAsset.description,
        tags: existingAsset.tags,
        favorite: existingAsset.favorite
      } : {
        name: baseDisplayName(file.name),
        category: FOLDER_TO_CATEGORY[folderKey] || "Data",
        version: "1.0",
        description: "",
        tags: [],
        favorite: false
      };

      if (keepIndependentLocal) {
        await createLocalRecord(file, identity, metadata);
        updateProgress(`import-local-${i}`, 1, "Done");
      }

      for (const key of accountKeys) {
        const context = getAccountContext(key);
        await uploadAssetToContext(context, file, identity, metadata, (value, label) => updateProgress(`import-${key}-${i}`, value, label));
        updateProgress(`import-${key}-${i}`, 1, "Done");
      }

      if (keepLocal && file.size <= LARGE_FILE_WARNING) {
        await putAssetCache({
          assetId: identity.assetId,
          contentHash,
          name: file.name,
          mime: file.type,
          blob: file
        });
      }
    }

    state.pendingImportFiles = [];
    await refreshLibrary({ reloadCloud: true });
    showNotice("Import completed. Exact duplicates were merged into existing logical assets.", "success");
    setTimeout(() => $("importDialog").close(), 400);
  } catch (error) {
    console.error(error);
    showNotice(`Import failed: ${error.message}`, "error");
  } finally {
    $("startImportBtn").disabled = false;
    setBusy(false);
  }
}

function openExport(asset) {
  state.exportingRecordKey = asset.key;
  $("exportFileSummary").innerHTML = `<strong>${escapeHtml(asset.name)}</strong><br>${escapeHtml(asset.originalName)} · ${escapeHtml(formatBytes(asset.size))}<br>${escapeHtml(locationText(asset))}`;
  $("exportLocal").checked = false;
  $("exportCategory").value = CATEGORY_OPTIONS.includes(asset.category) ? asset.category : "Data";
  $("exportProgress").innerHTML = "";
  renderAccountChecks("exportAccounts", "exportAccount");
  $("exportDialog").showModal();
}

function saveBlobLocally(blob, filename) {
  const url = registerObjectUrl(blob, 15000);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

async function startExport() {
  const asset = currentRecord(state.exportingRecordKey);
  if (!asset) return;
  const accountKeys = selectedCheckboxValues("exportAccount");
  const saveLocal = $("exportLocal").checked;
  const category = $("exportCategory").value;
  if (!saveLocal && accountKeys.length === 0) {
    showNotice("Select a local or OneDrive export destination.", "warn");
    return;
  }

  try {
    setBusy(true);
    $("startExportBtn").disabled = true;
    const blob = await ensureBlob(asset, { cache: true });
    const contentHash = asset.contentHash || await sha256Blob(blob);
    const rows = [];
    if (saveLocal) rows.push(progressRow("export-local-row", `${asset.originalName} → Local download`));
    for (const key of accountKeys) rows.push(progressRow(`export-${key}`, `${asset.originalName} → ${getAccountContext(key).email}`));
    $("exportProgress").innerHTML = rows.join("");

    if (saveLocal) {
      saveBlobLocally(blob, asset.originalName);
      updateProgress("export-local-row", 1, "Saved");
    }

    for (const key of accountKeys) {
      const context = getAccountContext(key);
      const existing = asset.cloudLocations.find(location => location.accountKey === key);
      if (existing) {
        if (existing.category !== category) {
          const destinationKey = CATEGORY_TO_FOLDER[category] || classifyFile(asset.originalName);
          await state.graph.moveItem(context.account, existing.itemId, context.rootKit.folders[destinationKey]);
        }
        await mutateManifest(state.graph, context, manifest => {
          manifest.items ||= {};
          manifest.items[existing.itemId] = {
            ...(manifest.items[existing.itemId] || {}),
            assetId: asset.assetId,
            contentHash,
            eTag: existing.eTag || "",
            displayName: asset.name,
            category,
            version: asset.version,
            description: asset.description,
            tags: asset.tags,
            favorite: asset.favorite,
            updatedAt: new Date().toISOString()
          };
        });
        updateProgress(`export-${key}`, 1, "Already saved");
        continue;
      }

      const file = new File([blob], asset.originalName, { type: asset.mime || blob.type });
      await uploadAssetToContext(context, file, { assetId: asset.assetId, contentHash }, {
        name: asset.name,
        category,
        version: asset.version,
        description: asset.description,
        tags: asset.tags,
        favorite: asset.favorite
      }, (value, label) => updateProgress(`export-${key}`, value, label));
      updateProgress(`export-${key}`, 1, "Done");
    }

    await refreshLibrary({ reloadCloud: true });
    showNotice("Export completed without creating duplicate cards.", "success");
    setTimeout(() => $("exportDialog").close(), 400);
  } catch (error) {
    console.error(error);
    showNotice(`Export failed: ${error.message}`, "error");
  } finally {
    $("startExportBtn").disabled = false;
    setBusy(false);
  }
}

async function replaceRecord(asset, file) {
  if (!confirm(`Replace the current contents of “${asset.name}” in every saved location?\n\nThe logical asset and card will remain the same.`)) return;
  try {
    setBusy(true);
    state.transferStates.set(asset.key, { type: "uploading", progress: 0 });
    renderCards();
    const contentHash = await sha256Blob(file);

    for (const location of asset.localLocations) {
      const local = await getLocalFile(location.localId);
      if (!local) continue;
      local.originalName = file.name;
      local.mime = file.type || "application/octet-stream";
      local.size = file.size;
      local.contentHash = contentHash;
      local.blob = file;
      local.modifiedAt = new Date().toISOString();
      await putLocalFile(local);
    }

    const grouped = new Map();
    for (const location of asset.cloudLocations) {
      if (!grouped.has(location.accountKey)) grouped.set(location.accountKey, []);
      grouped.get(location.accountKey).push(location);
    }
    for (const [key, locations] of grouped) {
      const context = getAccountContext(key);
      if (!context) continue;
      const updated = new Map();
      for (const location of locations) {
        let item = await state.graph.replaceItem(context.account, location.itemId, file, {
          onProgress: value => {
            state.transferStates.set(asset.key, { type: "uploading", progress: value });
            renderCards();
          }
        });
        if (file.name !== location.originalName) item = await state.graph.renameItem(context.account, location.itemId, file.name);
        updated.set(location.itemId, item);
      }
      await mutateManifest(state.graph, context, manifest => {
        manifest.items ||= {};
        for (const location of locations) {
          const item = updated.get(location.itemId);
          manifest.items[location.itemId] = {
            ...(manifest.items[location.itemId] || {}),
            assetId: asset.assetId,
            contentHash,
            eTag: item?.eTag || "",
            updatedAt: new Date().toISOString()
          };
        }
      });
    }

    if (file.size <= LARGE_FILE_WARNING) {
      await putAssetCache({ assetId: asset.assetId, contentHash, name: file.name, mime: file.type, blob: file });
    } else {
      await deleteAssetCache(asset.assetId);
    }
    state.transferStates.delete(asset.key);
    await refreshLibrary({ reloadCloud: true });
    showNotice("Asset replaced across all connected saved locations.", "success");
  } catch (error) {
    state.transferStates.delete(asset.key);
    showNotice(`Replace failed: ${error.message}`, "error");
  } finally {
    setBusy(false);
  }
}

function openDeleteDialog(asset) {
  state.deletingRecordKey = asset.key;
  $("deleteFileSummary").innerHTML = `<strong>${escapeHtml(asset.name)}</strong><br>${escapeHtml(locationText(asset))}`;
  const rows = [];
  if (asset.cache) rows.push(`<label><input type="checkbox" name="deleteLocation" value="cache"> <span class="account-detail"><strong>Downloaded browser cache</strong><small>Remove the cached copy on this device</small></span></label>`);
  for (const location of asset.localLocations) {
    rows.push(`<label><input type="checkbox" name="deleteLocation" value="local:${escapeHtml(location.localId)}"> <span class="account-detail"><strong>Local-only stored copy</strong><small>Browser IndexedDB on this device</small></span></label>`);
  }
  for (const location of asset.cloudLocations) {
    rows.push(`<label><input type="checkbox" name="deleteLocation" value="cloud:${escapeHtml(location.accountKey)}:${escapeHtml(location.itemId)}"> <span class="account-detail"><strong>${escapeHtml(location.accountEmail)}</strong><small>Delete the OneDrive file</small></span></label>`);
  }
  $("deleteLocations").innerHTML = rows.join("") || `<div class="muted">No removable locations were found.</div>`;
  $("deleteDialog").showModal();
}

async function deleteSelectedLocations() {
  const asset = currentRecord(state.deletingRecordKey);
  if (!asset) return;
  const selected = selectedCheckboxValues("deleteLocation");
  if (!selected.length) {
    showNotice("Select at least one location to remove.", "warn");
    return;
  }
  if (!confirm("Remove the selected locations? OneDrive deletions may be recoverable from the OneDrive recycle bin.")) return;

  try {
    setBusy(true);
    for (const value of selected) {
      if (value === "cache") {
        await deleteAssetCache(asset.assetId);
        continue;
      }
      if (value.startsWith("local:")) {
        await deleteLocalFile(value.slice("local:".length));
        continue;
      }
      if (value.startsWith("cloud:")) {
        const [, key, itemId] = value.split(":");
        const context = getAccountContext(key);
        if (!context) throw new Error("The OneDrive account for a selected location is not connected.");
        await state.graph.deleteItem(context.account, itemId);
        await mutateManifest(state.graph, context, manifest => {
          if (manifest.items) delete manifest.items[itemId];
        });
      }
    }
    $("deleteDialog").close();
    await refreshLibrary({ reloadCloud: true });
    showNotice("Selected locations removed.", "success");
  } catch (error) {
    showNotice(`Delete failed: ${error.message}`, "error");
  } finally {
    setBusy(false);
  }
}

async function handleCardAction(action, asset) {
  try {
    if (action === "open") return openRecord(asset);
    if (action === "preview") return previewRecord(asset);
    if (action === "edit") return openEdit(asset);
    if (action === "export") return openExport(asset);
    if (action === "favorite") return toggleFavorite(asset);
    if (action === "replace") {
      state.replacingRecordKey = asset.key;
      $("replaceInput").value = "";
      $("replaceInput").click();
      return;
    }
    if (action === "delete") return openDeleteDialog(asset);
  } catch (error) {
    showNotice(`${action} failed: ${error.message}`, "error");
  }
}

function closeDialog(id) {
  const dialog = $(id);
  if (dialog?.open) dialog.close();
  if (id === "previewDialog") revokePreviewUrls();
}

function bindEvents() {
  $("connectBtn").addEventListener("click", connectAccount);
  $("connectAnotherBtn").addEventListener("click", connectAccount);
  $("accountsBtn").addEventListener("click", () => { renderAccounts(); $("accountsDialog").showModal(); });
  $("configureClientBtn").addEventListener("click", () => $("clientDialog").showModal());
  $("redirectUriText").textContent = REDIRECT_URI;
  $("clientIdInput").value = CLIENT_ID;
  $("saveClientIdBtn").addEventListener("click", () => {
    const value = $("clientIdInput").value.trim();
    if (!/^[0-9a-fA-F-]{36}$/.test(value)) {
      showNotice("Enter a valid Microsoft application client ID.", "warn");
      return;
    }
    localStorage.setItem("qthub_ms_client_id", value);
    location.reload();
  });

  $("addFilesBtn").addEventListener("click", () => { $("fileInput").value = ""; $("fileInput").click(); });
  $("addFolderBtn").addEventListener("click", () => { $("folderInput").value = ""; $("folderInput").click(); });
  $("fileInput").addEventListener("change", event => openImport(event.target.files));
  $("folderInput").addEventListener("change", event => openImport(event.target.files));
  $("replaceInput").addEventListener("change", event => {
    const file = event.target.files?.[0];
    const asset = currentRecord(state.replacingRecordKey);
    if (file && asset) replaceRecord(asset, file);
  });

  $("refreshBtn").addEventListener("click", async () => {
    setBusy(true);
    await refreshLibrary({ reloadCloud: true });
    setBusy(false);
    showNotice("Library refreshed.", "success");
  });
  $("startImportBtn").addEventListener("click", startImport);
  $("saveEditBtn").addEventListener("click", saveEdit);
  $("startExportBtn").addEventListener("click", startExport);
  $("confirmDeleteBtn").addEventListener("click", deleteSelectedLocations);
  $("previewExportBtn").addEventListener("click", () => {
    const asset = currentRecord(state.previewRecordKey);
    if (asset) {
      closeDialog("previewDialog");
      openExport(asset);
    }
  });
  $("previewOpenBtn").addEventListener("click", () => {
    const asset = currentRecord(state.previewRecordKey);
    if (asset) openRecord(asset);
  });

  for (const id of ["searchInput", "accountFilter", "categoryFilter", "favoritesOnly"]) {
    $(id).addEventListener(id === "searchInput" ? "input" : "change", renderCards);
  }

  document.addEventListener("click", async event => {
    const closer = event.target.closest("[data-close]");
    if (closer) return closeDialog(closer.dataset.close);

    const actionButton = event.target.closest("[data-action]");
    if (actionButton) {
      const asset = currentRecord(actionButton.dataset.key);
      if (asset) await handleCardAction(actionButton.dataset.action, asset);
      return;
    }

    const accountButton = event.target.closest("[data-account-action]");
    if (accountButton) {
      const context = getAccountContext(accountButton.dataset.accountKey);
      if (!context) return;
      const action = accountButton.dataset.accountAction;
      try {
        if (action === "create-root") {
          setBusy(true);
          await createRootForContext(context);
          await refreshLibrary({ reloadCloud: false });
        } else if (action === "refresh") {
          setBusy(true);
          await loadAccount(context.account, { promptCreate: false });
          await refreshLibrary({ reloadCloud: false });
        } else if (action === "open-onedrive") {
          window.open(context.rootKit.root.webUrl, "_blank", "noopener");
        }
      } catch (error) {
        showNotice(`Account action failed: ${error.message}`, "error");
      } finally {
        setBusy(false);
      }
    }
  });

  const dropzone = $("dropzone");
  for (const type of ["dragenter", "dragover"]) {
    dropzone.addEventListener(type, event => { event.preventDefault(); dropzone.classList.add("active"); });
  }
  for (const type of ["dragleave", "drop"]) {
    dropzone.addEventListener(type, event => { event.preventDefault(); dropzone.classList.remove("active"); });
  }
  dropzone.addEventListener("drop", event => openImport(event.dataTransfer.files));

  window.addEventListener("unload", revokeObjectUrls);
}

async function init() {
  bindEvents();
  renderCategoryOptions();
  await openDatabase();
  await migrateLocalStorage();
  await refreshLibrary({ reloadCloud: false });
  try {
    await initializeAuth();
    await refreshLibrary({ reloadCloud: false });
  } catch (error) {
    console.error(error);
    showNotice(`Initialization failed: ${error.message}`, "error");
  }

  if ("serviceWorker" in navigator && window.location.protocol !== "file:") {
    navigator.serviceWorker.register("./sw.js").catch(error => console.warn("Service worker registration failed", error));
  }
  console.info(`${APP_NAME} ${APP_VERSION} ready`);
}

init();
